import styled from '@emotion/native';
import {
  ConversationMessage,
  ConversationSendMessage,
  MessageType,
} from '@quromedical/communication';
import { ConversationSendMessageRequest } from '@quromedical/communication/server';
import { compact, first } from '@quromedical/utils';
import { Col, Row } from 'components/base';
import { FileResult } from 'components/types';
import {
  Card,
  Button,
  DEFAULT_INPUT_HEIGHT,
  DEFAULT_MULTILINE_HEIGHT,
  FilePickerDialog,
  Icon,
  MARGIN,
  Spacer,
  DebounceButton,
  Spinner,
} from 'design-system';
import { logger } from 'helpers';
import { useCommunications } from 'hooks/useCommunication';
import { useFileUpload } from 'hooks/useFileUpload';
import { useLayoutDimensions } from 'hooks/useLayoutDimensions';
import { useIsSmallerThan } from 'hooks/useResponsive';
import { useToggle } from 'hooks/useToggle';
import { CommunicationApi, MediaApi } from 'integration/aggregate';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { View, useWindowDimensions } from 'react-native';
import { GiftedChat, IMessage, User } from 'react-native-gifted-chat';
import { strings } from 'strings';

import { ChatBottomSheet, MessageTemplateFetcher } from './chatBottomSheet';
import { ChatBubble, ChatEmptyMessage } from './ChatBubble';
import { ChatForm, isDisabled } from './ChatForm';
import { ChatSkeleton } from './ChatSkeleton';
import { messageProcessingMap, userProfile } from './internal';

const mediaApi = new MediaApi();
const communicationApi = new CommunicationApi();

interface ChatRoomCardProps {
  patientId: string;
  nextOfKinId?: string;
}

interface SizeProps {
  size?: number;
  hasMultiLine: boolean;
}
const BaseCard = styled(Card)<SizeProps>(({ size = 0, hasMultiLine = false }) => {
  const { height } = useWindowDimensions();
  const isSmall = useIsSmallerThan('sm');

  const padding = 10;
  const textInputOffsetHeight = DEFAULT_MULTILINE_HEIGHT - DEFAULT_INPUT_HEIGHT;
  const hasAddMultiLine = hasMultiLine ? textInputOffsetHeight : 0;

  const screenHeight = height - size - padding - hasAddMultiLine;
  const smallScreenHeight = height - padding - hasAddMultiLine;

  return {
    height: isSmall ? smallScreenHeight : screenHeight,
  };
});

const user: User = {
  _id: strings.CommunicationUserSystem,
  name: 'Not available',
};

const mapCommunicationToGifted = (
  conversationMessages: ConversationMessage[] | undefined
): IMessage[] => {
  if (!conversationMessages) {
    return [];
  }

  //TODO this should be handled by the API
  const orderedList = conversationMessages.sort(
    (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
  );

  const iMessages = orderedList.map<IMessage | undefined>((conversationMessage, i) => {
    if (conversationMessage.type === MessageType.Text) {
      const { sender, text, createdAt, delivered, read } = conversationMessage;

      const textMessage: IMessage = {
        _id: i,
        text: text,
        user: userProfile(sender),
        createdAt: new Date(createdAt),
        received: read,
        sent: delivered,
      };
      return textMessage;
    }

    return messageProcessingMap[conversationMessage.contentType](conversationMessage, i);
  });

  return compact(iMessages);
};

export const ChatRoomCard: React.FC<ChatRoomCardProps> = ({ patientId, nextOfKinId }) => {
  const [isLoadingSubmit, setLoadingSubmit] = useState<boolean>(false);
  const { layout, onLayout } = useLayoutDimensions({});
  const [templateMessage, setTemplateMessage] = useState<MessageTemplateFetcher>();
  const [open, toggleOpen] = useToggle(false);
  //TODO make the dynamic multiline work
  const [multiLine, setMultiLine] = useState<boolean>(false);
  const [screenOffsetHeight, setScreenOffsetHeight] = useState<number>(0);
  const charCardRef = useRef<View>(null);
  const [openFilePicker, setOpenFilePicker] = useState<boolean>(false);
  const [uploadFiles, setUploadFiles] = useState<FileResult[]>([]);
  const upload = useFileUpload(data => mediaApi.createUploadUrl(patientId, data), patientId);

  const { conversations, isLoading, refetch } = useCommunications(patientId, nextOfKinId);

  const sendMessage = async (data: ConversationSendMessageRequest) => {
    setLoadingSubmit(true);

    try {
      if ('message' in data) {
        if (data.message.type === MessageType.Text) {
          await communicationApi.sendMessage(data);
        }
      } else if ('template' in data) {
        await communicationApi.sendTemplateMessage(data);
      }

      setTemplateMessage(undefined);
      await refetch();
    } catch (error) {
      logger.error(error);
    } finally {
      setLoadingSubmit(false);
      setMultiLine(false);
      setTemplateMessage(undefined);
    }
  };

  const restMedia = () => {
    setOpenFilePicker(false);
    setUploadFiles([]);
  };

  const sendMediaMessage = useCallback(async () => {
    setLoadingSubmit(true);

    try {
      const mediaItem = first(upload.media);
      if (!mediaItem) {
        return restMedia();
      }
      const messageBody: ConversationSendMessage = {
        message: {
          type: MessageType.Media,
          contentType: mediaItem.contentType,
          mediaKey: mediaItem.key,
          title: mediaItem.title,
        },
        patient: patientId,
      };

      const message: ConversationSendMessage = nextOfKinId
        ? { ...messageBody, nextOfKin: nextOfKinId }
        : { ...messageBody };

      await communicationApi.sendMessage(message);

      restMedia();
      setTemplateMessage(undefined);
      setLoadingSubmit(false);
      await refetch();
    } catch (error) {
      logger.error(error);
      setLoadingSubmit(false);
      restMedia();
    } finally {
      setLoadingSubmit(false);
      restMedia();
    }
  }, [patientId, refetch, upload.media, nextOfKinId]);

  const giftedChatMessages = useMemo(() => {
    if (!conversations) {
      return [];
    }

    return mapCommunicationToGifted(conversations).reverse();
  }, [conversations]);

  const renderTicks = (message: IMessage) => {
    if (message.user._id !== strings.CommunicationUserSystem) {
      return null;
    }
    const showTicks = message.received && message.sent;
    const showTwoGreyTicks = !message.received && message.sent;

    if (showTwoGreyTicks) {
      return (
        <View style={{ paddingRight: 5 }}>
          <Icon name={'done-all'} color={'message-pending'} size={14} />
        </View>
      );
    }

    return (
      <View style={{ paddingRight: 5 }}>
        <Icon
          name={showTicks ? 'done-all' : 'done'}
          color={showTicks ? 'message-confirmation' : 'message-pending'}
          size={14}
        />
      </View>
    );
  };

  const loading = isLoading || isLoadingSubmit;

  const calculateScreenOffsetHeight = useCallback(() => {
    charCardRef?.current?.measure((fx, fy, width, height, px, py) => {
      return setScreenOffsetHeight(py);
    });
  }, []);
  calculateScreenOffsetHeight();

  const onFilesChange = useCallback(
    (media: FileResult[]) => {
      upload.onFilesChange({ media });
      setUploadFiles(media);
    },
    [upload]
  );

  const closeFiles = useCallback(() => {
    restMedia();
  }, []);

  const filePickerDisabled = !upload.uploadsComplete || uploadFiles.length >= 1;
  const hideAttachmentIfSessionExpired = isDisabled(conversations || []);

  return (
    <>
      <BaseCard
        size={screenOffsetHeight}
        ref={charCardRef}
        hasMultiLine={multiLine}
        paddingHorizontal={0}
        paddingVertical={0}
        onLayout={onLayout}
      >
        <GiftedChat
          messages={giftedChatMessages}
          user={user}
          inverted={true}
          renderAvatar={null}
          showUserAvatar={false}
          showAvatarForEveryMessage={false}
          isLoadingEarlier={true}
          renderChatEmpty={() => (loading ? <ChatSkeleton /> : <ChatEmptyMessage />)}
          renderBubble={props => <ChatBubble renderTicks={renderTicks} {...props} />}
          renderInputToolbar={() =>
            openFilePicker ? (
              <Card border paddingVertical={0}>
                {!isLoadingSubmit ? (
                  <Col>
                    <Spacer height={MARGIN['2xs']} />
                    <Row flex={1} alignItems="center" justifyContent="center" fullWidth>
                      <FilePickerDialog
                        isOpen={openFilePicker}
                        isDisabled={filePickerDisabled}
                        onChange={onFilesChange}
                        value={uploadFiles}
                        progress={upload.progress}
                        uploadInProgress={!upload.uploadsComplete}
                        buttonShape="round"
                        allowMultipleFiles={false}
                        onCancel={closeFiles}
                        rowReverse={true}
                      />
                      <Row justifyContent="center">
                        <Button
                          shape={'round'}
                          variant="flat-alt"
                          icon={'close'}
                          onPress={closeFiles}
                        />
                        <Spacer width={MARGIN.xs} />
                        <DebounceButton shape={'round'} icon={'send'} onPress={sendMediaMessage} />
                      </Row>
                    </Row>
                  </Col>
                ) : (
                  <Spinner />
                )}
              </Card>
            ) : (
              <>
                {!isLoadingSubmit ? (
                  <ChatForm
                    onSubmit={sendMessage}
                    patientId={patientId}
                    nextOfKinId={nextOfKinId}
                    conversationHistory={conversations || []}
                    onAttachmentSubmit={toggleOpen}
                    templateMessage={templateMessage}
                    multiLine={multiLine ? multiLine : false}
                  />
                ) : (
                  <Spinner />
                )}
              </>
            )
          }
        />
      </BaseCard>

      <ChatBottomSheet
        patientId={patientId}
        layout={layout}
        openBottomSheet={open}
        setMultiLine={setMultiLine}
        toggleBottomSheet={toggleOpen}
        openFilePicker={setOpenFilePicker}
        templateMessage={setTemplateMessage}
        hideAttachmentIfSessionExpired={hideAttachmentIfSessionExpired}
      />
    </>
  );
};
